import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Building2, Sliders, Users, ShieldCheck, LogOut, Menu,
  Sun, Moon, Compass, ChevronDown, CheckSquare, ScrollText, Globe, Languages,
  Server, CreditCard, Contact, Route, FileText,
  FileSpreadsheet, Receipt, Ticket, FileCheck2, Lock,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useEntitlements } from "@/context/EntitlementContext";
import { FEATURE_META } from "@/components/FeatureLocked";
import { useI18n } from "@/context/I18nContext";

function initials(name) {
  if (!name) return "??";
  return name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
}

export default function AppShell() {
  const { user, logout, hasPermission } = useAuth();
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dark, setDark] = useState(() => localStorage.getItem("tf-theme") !== "light");
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  useEffect(() => {
    const root = document.documentElement;
    if (dark) root.classList.add("dark");
    else root.classList.remove("dark");
    localStorage.setItem("tf-theme", dark ? "dark" : "light");
  }, [dark]);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const isPlatform = user?.is_platform;
  const scopeLabel = user?.scope === "platform" ? "Platform" : "Company";
  const { hasFeature } = useEntitlements();
  const F = (arr) => arr.filter((n) => hasPermission(n.perm));
  const groups = isPlatform
    ? [
        { title: "Operating System", items: [
          { label: "Operations Dashboard", path: "/platform", icon: Server, perm: "platform.dashboard.view", testid: "sidebar-nav-platform" },
          { label: "Platform Console", icon: Compass, perm: "platform.dashboard.view", testid: "sidebar-nav-platform-console", disabled: true },
        ] },
        { title: "Platform Management", items: [
          { label: "Customer Organizations", path: "/platform/customers", icon: Building2, perm: "platform.dashboard.view", testid: "sidebar-nav-customer-orgs" },
          { label: "Pricing & Plans", path: "/platform/pricing", icon: CreditCard, perm: "platform.billing.view", testid: "sidebar-nav-pricing" },
          { label: "Users Management", path: "/company/users", icon: Users, perm: "users.view", testid: "sidebar-nav-users" },
          { label: "Roles & Permissions", path: "/company/roles", icon: ShieldCheck, perm: "roles.view", testid: "sidebar-nav-roles" },
          { label: "Company Profile", path: "/company/profile", icon: Building2, perm: "company.view", testid: "sidebar-nav-company-profile" },
        ] },
        { title: "Governance", items: [
          { label: "Approvals", path: "/approvals", icon: CheckSquare, perm: "approvals.view", testid: "sidebar-nav-approvals" },
          { label: "Audit Log", path: "/audit", icon: ScrollText, perm: "audit.view", testid: "sidebar-nav-audit" },
          { label: "Language", path: "/settings/language", icon: Languages, perm: "company_config.view", testid: "sidebar-nav-language" },
        ] },
      ]
    : [
        { title: "Workspace", items: F([
          { label: "Operations Dashboard", path: "/dashboard", icon: LayoutDashboard, perm: "dashboard.view", testid: "sidebar-nav-dashboard" },
          { label: "Customers", path: "/customers", icon: Contact, perm: "customers.view", testid: "sidebar-nav-customers", feature: "customer_management" },
          { label: "Inquiries", path: "/inquiries", icon: Route, perm: "inquiries.view", testid: "sidebar-nav-inquiries", feature: "inquiry_management" },
          { label: "Quotations", path: "/quotations", icon: FileText, perm: "quotations.view", testid: "sidebar-nav-quotations", feature: "quotations" },
          { label: "Pro Forma Invoices", path: "/finance/proforma-invoices", icon: FileSpreadsheet, perm: "proformas.view", testid: "sidebar-nav-proformas", feature: "basic_invoice_receipt" },
          { label: "Payments", path: "/finance/payments", icon: Receipt, perm: "payments.view", testid: "sidebar-nav-payments", feature: "basic_payment_tracking" },
          { label: "Bookings", path: "/finance/bookings", icon: Ticket, perm: "bookings.view", testid: "sidebar-nav-bookings", feature: "booking_management" },
          { label: "Invoices", path: "/finance/invoices", icon: FileCheck2, perm: "invoices.view", testid: "sidebar-nav-invoices", feature: "basic_invoice_receipt" },
          { label: "Company Profile", path: "/company/profile", icon: Building2, perm: "company.view", testid: "sidebar-nav-company-profile" },
          { label: "Company Configuration", path: "/company/config", icon: Sliders, perm: "company_config.view", testid: "sidebar-nav-company-config" },
          { label: "Users Management", path: "/company/users", icon: Users, perm: "users.view", testid: "sidebar-nav-users" },
          { label: "Roles & Permissions", path: "/company/roles", icon: ShieldCheck, perm: "roles.view", testid: "sidebar-nav-roles" },
        ]) },
        { title: "Governance", items: F([
          { label: "Approvals", path: "/approvals", icon: CheckSquare, perm: "approvals.view", testid: "sidebar-nav-approvals", feature: "approval_workflows" },
          { label: "Audit Log", path: "/audit", icon: ScrollText, perm: "audit.view", testid: "sidebar-nav-audit", feature: "governance_audit" },
          { label: "Language", path: "/settings/language", icon: Languages, perm: "company_config.view", testid: "sidebar-nav-language" },
        ]) },
      ];

  const NavItem = ({ n }) => {
    const Icon = n.icon;
    if (n.disabled) {
      return (
        <div data-testid={n.testid} aria-disabled="true"
          className="flex cursor-not-allowed items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-500 opacity-60">
          <Icon className="h-[18px] w-[18px] shrink-0" />
          <span className="truncate">{n.label}</span>
          <span className="ml-auto rounded bg-ink-panel px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-ink-dim">Soon</span>
        </div>
      );
    }
    // Plan-entitlement lock: RBAC may allow it, but the tenant's plan may not include it.
    // Locked items stay visible with an upgrade hint instead of silently disappearing.
    const locked = n.feature && !hasFeature(n.feature);
    if (locked) {
      const meta = FEATURE_META[n.feature] || {};
      return (
        <NavLink to={`/upgrade?feature=${n.feature}`} data-testid={n.testid} onClick={() => setMobileOpen(false)}
          className="group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-500 transition-colors hover:bg-ink-panel hover:text-slate-300">
          <Icon className="h-[18px] w-[18px] shrink-0" />
          <span className="truncate">{n.label}</span>
          <span className="ml-auto inline-flex items-center gap-1 rounded bg-amber-500/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-amber-500" data-testid={`nav-lock-${n.feature}`}>
            <Lock className="h-3 w-3" />{meta.short || "Upgrade"}
          </span>
        </NavLink>
      );
    }
    return (
      <NavLink to={n.path} data-testid={n.testid} onClick={() => setMobileOpen(false)}
        className={({ isActive }) =>
          `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
            isActive ? "bg-ink-gold/10 text-ink-gold" : "text-slate-300 hover:bg-ink-panel hover:text-ink-text"}`}>
        <Icon className="h-[18px] w-[18px] shrink-0" />
        <span className="truncate">{n.label}</span>
      </NavLink>
    );
  };

  const SidebarInner = () => (
    <div className="flex h-full flex-col">
      <div className="flex h-16 items-center gap-2.5 border-b border-ink-line px-5">
        <img src="/brand/petrinos-p-symbol.png" alt="PETRINOS" data-testid="sidebar-p-symbol"
          className="h-9 w-9 rounded-lg object-cover" />
        <div className="leading-tight">
          <div className="font-display text-base font-extrabold tracking-tight text-ink-text">PETRINOS Travel OS</div>
          <div className="text-[10px] font-medium uppercase tracking-widest text-slate-400">Travel Operating System</div>
        </div>
      </div>
      <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4" data-testid="sidebar-nav">
        {groups.map((g, gi) => (
          <div key={g.title} className={gi > 0 ? "pt-4" : ""}>
            <div className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-slate-500">{g.title}</div>
            {g.items.map((n) => <NavItem key={n.path || n.testid} n={n} />)}
          </div>
        ))}
      </nav>
      <div className="border-t border-ink-line p-3">
        <div className="flex items-center gap-2 rounded-lg bg-ink-panel/50 px-3 py-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-ink-gold/15 text-xs font-bold text-ink-gold">
            {initials(user?.company?.brand_name || "PE")}
          </div>
          <div className="min-w-0 leading-tight">
            <div className="truncate text-xs font-semibold text-ink-text" data-testid="active-company-name">
              {user?.company?.brand_name || "PETRINOS Travel"}
            </div>
            <div className="text-[10px] text-slate-400">{t("active_company")}</div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen w-full overflow-hidden bg-slate-50 dark:bg-ink">
      <aside className="hidden w-64 shrink-0 bg-ink-deep lg:block"><SidebarInner /></aside>
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" data-testid="mobile-sidebar">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-64 bg-ink-deep"><SidebarInner /></aside>
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-16 shrink-0 items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 dark:border-ink-line dark:bg-ink-card sm:px-6">
          <div className="flex items-center gap-3">
            <button className="rounded-md p-2 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-ink-panel lg:hidden"
              onClick={() => setMobileOpen(true)} data-testid="mobile-menu-button" aria-label="Open menu">
              <Menu className="h-5 w-5" />
            </button>
            <div className="hidden sm:block">
              <div className="text-xs font-medium uppercase tracking-wider text-slate-400">{t("internal_ops")}</div>
              <div className="font-display text-sm font-semibold text-slate-800 dark:text-ink-text">
                {user?.company?.legal_name || "PETRINOS Travel Operating System"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <button onClick={() => setLangOpen((o) => !o)} data-testid="language-switcher"
                className="flex items-center gap-1.5 rounded-md px-2 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-ink-panel">
                <Languages className="h-5 w-5" /><span className="uppercase">{lang}</span>
              </button>
              {langOpen && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setLangOpen(false)} />
                  <div className="absolute right-0 z-40 mt-2 w-40 rounded-lg border border-slate-200 bg-white p-1 shadow-xl dark:border-ink-line dark:bg-ink-panel">
                    {[["en", "English"], ["id", "Bahasa Indonesia"]].map(([code, label]) => (
                      <button key={code} data-testid={`lang-option-${code}`}
                        onClick={() => { setLang(code); setLangOpen(false); }}
                        className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-sm ${lang === code ? "bg-sky-50 text-sky-700 dark:bg-ink-gold/10 dark:text-ink-gold" : "text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-ink-line"}`}>
                        {label}<span className="text-[10px] uppercase text-slate-400">{code}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            <button onClick={() => setDark((d) => !d)} data-testid="theme-toggle" aria-label="Toggle theme"
              className="rounded-md p-2 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-ink-panel">
              {dark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>
            <div className="relative">
              <button onClick={() => setMenuOpen((o) => !o)} data-testid="user-menu-button"
                className="flex items-center gap-2 rounded-lg py-1.5 pl-1.5 pr-2 hover:bg-slate-100 dark:hover:bg-ink-panel">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-ink-gold text-xs font-bold text-[#0B1120]">{initials(user?.name)}</div>
                <div className="hidden text-left leading-tight sm:block">
                  <div className="text-xs font-semibold text-slate-800 dark:text-ink-text">{user?.name}</div>
                  <div className="text-[10px] text-slate-400">{scopeLabel} scope</div>
                </div>
                <ChevronDown className="h-4 w-4 text-slate-400" />
              </button>
              {menuOpen && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setMenuOpen(false)} />
                  <div className="absolute right-0 z-40 mt-2 w-60 rounded-xl border border-slate-200 bg-white p-1.5 shadow-xl dark:border-ink-line dark:bg-ink-panel" data-testid="user-menu">
                    <div className="px-3 py-2">
                      <div className="text-sm font-semibold text-slate-800 dark:text-ink-text">{user?.name}</div>
                      <div className="truncate text-xs text-slate-500">{user?.email}</div>
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {(user?.role_keys || []).map((r) => (
                          <span key={r} className="rounded bg-sky-50 px-1.5 py-0.5 text-[10px] font-medium text-sky-700 dark:bg-ink-gold/10 dark:text-ink-gold">{r}</span>
                        ))}
                      </div>
                    </div>
                    <div className="my-1 h-px bg-slate-100 dark:bg-ink-panel" />
                    <button onClick={doLogout} data-testid="logout-button"
                      className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10">
                      <LogOut className="h-4 w-4" /> {t("signout")}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8" data-testid="main-content"><Outlet /></main>
      </div>
    </div>
  );
}
